import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Divider,
  Drawer,
  IconButton,
  InputAdornment,
  Paper,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import SaveIcon from "@mui/icons-material/Save";
import SearchIcon from "@mui/icons-material/Search";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import FolderIcon from "@mui/icons-material/Folder";
import DescriptionIcon from "@mui/icons-material/Description";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import { Airframe } from "../../../../types/airframe";
import { useApiWithHandling } from "../../../../hooks/useApiWithHandling";
import { useSnackbar } from "../../../../contexts/snackbar-context";
import { useTenantManagementService } from "../../../../services/tenantManagementService";
import {
  MiniPackGenerationDocumentRequest,
  MiniPackGenerationRequest,
  useMiniPackGenerationService,
} from "../../../../services/miniPackGenerationService";
import { jobMatchesAirframeScope } from "../../../../utils/miniPackExportJobUtils";
import { useMiniPackReportService } from "../../../../services/miniPackReportService";
import { isHtmlDocument } from "../../../../utils/htmlDocumentUtils";

type SearchDocument = {
  id: string;
  title?: string;
  fileName?: string;
};

type SearchFolder = {
  name: string;
  documents: SearchDocument[];
  children: SearchFolder[];
};

type SearchDocumentsResponse = {
  data?: {
    folders?: SearchFolder[];
  };
};

type DocumentOption = {
  documentId: string;
  fileName: string;
  displayName: string;
  folderPath: string;
};

type FolderTreeNode = {
  key: string;
  name: string;
  path: string;
  documents: DocumentOption[];
  children: FolderTreeNode[];
};

type SectionState = {
  templateSectionId: string;
  sectionKey: string;
  sectionName: string;
  documents: MiniPackGenerationDocumentRequest[];
};

type Props = {
  open: boolean;
  airframe: Airframe | null;
  onClose: () => void;
  onOpenJobs: (airframe: Airframe) => void;
};

const SUPPORTED_MINI_PACK_EXTENSIONS = new Set([".pdf", ".png", ".jpg", ".jpeg", ".txt"]);

const isSupportedForMiniPack = (fileName: string): boolean => {
  const extension = fileName.substring(fileName.lastIndexOf(".")).toLowerCase();
  return SUPPORTED_MINI_PACK_EXTENSIONS.has(extension);
};

const COLORS = {
  white: "#fff",
  primary: "hsl(19 101% 51% / 1)",
  primaryHover: "hsl(19 101% 41% / 1)",
  panelBg: "#1a1a1a",
  surfaceBg: "#111",
  border: "#333",
  mutedText: "rgba(255,255,255,0.72)",
};

const searchFieldSx = {
  mb: 1,
  "& .MuiInputLabel-root": {
    color: COLORS.mutedText,
  },
  "& .MuiInputLabel-root.Mui-focused": {
    color: COLORS.primary,
  },
  "& .MuiOutlinedInput-root": {
    color: COLORS.white,
    backgroundColor: COLORS.surfaceBg,
    "& fieldset": {
      borderColor: "#666",
    },
    "&:hover fieldset": {
      borderColor: "#999",
    },
    "&.Mui-focused fieldset": {
      borderColor: COLORS.primary,
    },
  },
  "& .MuiInputBase-input::placeholder": {
    color: COLORS.mutedText,
    opacity: 1,
  },
  "& .MuiSvgIcon-root": {
    color: COLORS.white,
  },
};

const documentTreeContainerSx = {
  flex: 1,
  minHeight: 0,
  overflowY: "auto" as const,
  border: `1px solid ${COLORS.border}`,
  borderRadius: 1,
  backgroundColor: COLORS.surfaceBg,
};

const documentTreeRowSx = (depth: number) => ({
  display: "flex",
  alignItems: "center",
  gap: 0.5,
  px: 1,
  py: 0.5,
  borderBottom: `1px solid ${COLORS.border}`,
  pl: 1 + depth * 2,
  "&:hover": {
    backgroundColor: COLORS.panelBg,
  },
});

const buildFolderTree = (
  folders: SearchFolder[],
  parentPath = "",
  parentKey = "root",
  seenDocumentIds = new Set<string>()
): FolderTreeNode[] => {
  return folders.map((folder, index) => {
    const folderName = (folder.name || `Folder ${index + 1}`).trim();
    const path = parentPath ? `${parentPath} / ${folderName}` : folderName;
    const key = `${parentKey}/${folderName}-${index}`;

    const documents = (folder.documents || []).reduce<DocumentOption[]>((acc, doc) => {
      const id = (doc.id || "").trim();
      if (!id || seenDocumentIds.has(id)) {
        return acc;
      }

      const fileName = (doc.fileName || doc.title || "Untitled").trim();
      if (isHtmlDocument(fileName)) {
        return acc;
      }

      seenDocumentIds.add(id);
      acc.push({
        documentId: id,
        fileName,
        displayName: (doc.title || fileName || id).trim(),
        folderPath: path,
      });
      return acc;
    }, []);

    return {
      key,
      name: folderName,
      path,
      documents,
      children: buildFolderTree(folder.children || [], path, key, seenDocumentIds),
    };
  });
};

const flattenTreeDocuments = (nodes: FolderTreeNode[]): DocumentOption[] => {
  const output: DocumentOption[] = [];

  nodes.forEach((node) => {
    output.push(...node.documents);
    if (node.children.length > 0) {
      output.push(...flattenTreeDocuments(node.children));
    }
  });

  return output;
};

const filterFolderTree = (nodes: FolderTreeNode[], term: string): FolderTreeNode[] => {
  if (!term) {
    return nodes;
  }

  return nodes.reduce<FolderTreeNode[]>((acc, node) => {
    const folderMatches = node.name.toLowerCase().includes(term) || node.path.toLowerCase().includes(term);
    if (folderMatches) {
      acc.push(node);
      return acc;
    }

    const filteredDocuments = node.documents.filter((doc) => {
      return doc.fileName.toLowerCase().includes(term)
        || doc.displayName.toLowerCase().includes(term)
        || doc.folderPath.toLowerCase().includes(term);
    });

    const filteredChildren = filterFolderTree(node.children, term);

    if (filteredDocuments.length > 0 || filteredChildren.length > 0) {
      acc.push({
        ...node,
        documents: filteredDocuments,
        children: filteredChildren,
      });
    }

    return acc;
  }, []);
};

const collectFolderKeys = (nodes: FolderTreeNode[]): string[] => {
  const output: string[] = [];
  nodes.forEach((node) => {
    output.push(node.key);
    if (node.children.length > 0) {
      output.push(...collectFolderKeys(node.children));
    }
  });
  return output;
};

const collectSelectableDocumentIds = (node: FolderTreeNode): string[] => {
  const ids = node.documents
    .filter((doc) => isSupportedForMiniPack(doc.fileName))
    .map((doc) => doc.documentId);
  node.children.forEach((child) => {
    ids.push(...collectSelectableDocumentIds(child));
  });
  return ids;
};

export const MiniPackAirframeDrawer: React.FC<Props> = ({ open, airframe, onClose, onOpenJobs }) => {
  const { post } = useApiWithHandling();
  const { showError, showSuccess } = useSnackbar();
  const { createJob, listJobs, downloadJob } = useMiniPackGenerationService();
  const { getConfiguration, saveConfiguration } = useMiniPackReportService();
  const { getMiniPackTemplates, getMiniPackTemplateSections } = useTenantManagementService();

  const requiredTemplateType = "Airframe" as const;
  const tailNumber = String(airframe?.tailNumber || "").trim();
  const scopeLabel = tailNumber || "Airframe";

  const [sections, setSections] = useState<SectionState[]>([]);
  const [loadingDocuments, setLoadingDocuments] = useState(false);
  const [checkingTemplate, setCheckingTemplate] = useState(false);
  const [loadingTemplateSections, setLoadingTemplateSections] = useState(false);
  const [templateAvailable, setTemplateAvailable] = useState(false);
  const [templateFileName, setTemplateFileName] = useState<string>("");
  const [creatingJob, setCreatingJob] = useState(false);
  const [folderTree, setFolderTree] = useState<FolderTreeNode[]>([]);
  const [activeSectionIndex, setActiveSectionIndex] = useState<number>(0);
  const [pickerFilter, setPickerFilter] = useState("");
  const [selectedDocumentIds, setSelectedDocumentIds] = useState<Set<string>>(new Set());
  const [expandedFolderKeys, setExpandedFolderKeys] = useState<Set<string>>(new Set());
  const [savingConfiguration, setSavingConfiguration] = useState(false);
  const [hasPreviousMiniPack, setHasPreviousMiniPack] = useState(false);
  const [latestJob, setLatestJob] = useState<any>(null);

  const buildConfigurationPayload = useCallback((nextSections: SectionState[]) => ({
    sourceScope: "airframe" as const,
    tailNumber,
    sections: nextSections.map((section) => ({
      templateSectionId: section.templateSectionId,
      sectionKey: section.sectionKey,
      sectionName: section.sectionName,
      sortOrder: 0,
      documents: section.documents,
    })),
  }), [tailNumber]);

  const persistConfiguration = useCallback(async (nextSections: SectionState[]) => {
    if (!tailNumber) {
      return;
    }

    await saveConfiguration(buildConfigurationPayload(nextSections));
  }, [buildConfigurationPayload, saveConfiguration, tailNumber]);

  useEffect(() => {
    if (!open) {
      setSections([]);
      setFolderTree([]);
      setExpandedFolderKeys(new Set());
      setTemplateAvailable(false);
      setTemplateFileName("");
      setCheckingTemplate(false);
      setLoadingTemplateSections(false);
      setLoadingDocuments(false);
      setActiveSectionIndex(0);
      setPickerFilter("");
      setSelectedDocumentIds(new Set());
      return;
    }

    if (!airframe) {
      return;
    }

    setActiveSectionIndex(0);
    setPickerFilter("");
    setSelectedDocumentIds(new Set());
  }, [open, airframe]);

  useEffect(() => {
    if (!open || !airframe || !tailNumber) {
      setHasPreviousMiniPack(false);
      return undefined;
    }

    let cancelled = false;

    void listJobs(200)
      .then((items) => {
        if (cancelled) {
          return;
        }

        const matchingJob = items.find((job) => jobMatchesAirframeScope(job, tailNumber));
        setHasPreviousMiniPack(!!matchingJob);
        setLatestJob(matchingJob || null);
      })
      .catch(() => {
        if (!cancelled) {
          setHasPreviousMiniPack(false);
          setLatestJob(null);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [open, airframe, tailNumber, listJobs]);

  useEffect(() => {
    if (!open || !airframe) {
      return;
    }

    let cancelled = false;

    const initializeDrawer = async () => {
      setCheckingTemplate(true);
      setLoadingTemplateSections(false);
      setTemplateAvailable(false);
      setTemplateFileName("");
      setSections([]);

      try {
        const templates = await getMiniPackTemplates();
        if (cancelled) {
          return;
        }

        const matchedTemplate = templates.find((item) => item.templateType === requiredTemplateType);
        if (!matchedTemplate) {
          setTemplateAvailable(false);
          setTemplateFileName("");
          return;
        }

        setTemplateAvailable(true);
        setTemplateFileName(matchedTemplate.fileName || "");
        setLoadingTemplateSections(true);

        const response = await getMiniPackTemplateSections(requiredTemplateType);
        if (cancelled) {
          return;
        }

        const templateSections = (response.sections || [])
          .slice()
          .sort((left, right) => left.sortOrder - right.sortOrder)
          .map((section, index) => ({
            templateSectionId: section.id || "",
            sectionKey: section.sectionKey || `section-${index + 1}`,
            sectionName: section.sectionName || `Section ${index + 1}`,
            documents: [] as MiniPackGenerationDocumentRequest[],
          }));

        let resolvedSections = templateSections;
        if (tailNumber) {
          try {
            const savedConfiguration = await getConfiguration({
              sourceScope: "airframe",
              tailNumber,
            });
            if (savedConfiguration?.hasSavedConfiguration) {
              const documentsBySectionId = new Map(
                (savedConfiguration.sections || []).map((section) => [
                  section.templateSectionId.toLowerCase(),
                  section.documents || [],
                ])
              );

              resolvedSections = templateSections.map((section) => ({
                ...section,
                documents: documentsBySectionId.get(section.templateSectionId.toLowerCase()) ?? [],
              }));
            }
          } catch {
            resolvedSections = templateSections;
          }
        }

        setSections(resolvedSections);
        setActiveSectionIndex(0);
        setSelectedDocumentIds(new Set(
          resolvedSections[0]?.documents.map((document) => document.documentId) ?? []
        ));
      } catch {
        if (!cancelled) {
          setTemplateAvailable(false);
          setTemplateFileName("");
          setSections([]);
        }
      } finally {
        if (!cancelled) {
          setCheckingTemplate(false);
          setLoadingTemplateSections(false);
        }
      }
    };

    void initializeDrawer();

    return () => {
      cancelled = true;
    };
  }, [open, airframe, tailNumber, getConfiguration, getMiniPackTemplates, getMiniPackTemplateSections]);

  const isInitializing = checkingTemplate || loadingTemplateSections;
  const showWorkspace = Boolean(checkingTemplate || templateAvailable);
  const showPlaceholder = Boolean(!isInitializing && !templateAvailable);
  const canLoadDocuments = Boolean(open && airframe && templateAvailable && !isInitializing && tailNumber);

  const loadDocuments = useCallback(async () => {
    if (!open || !airframe || !canLoadDocuments) {
      return;
    }

    setLoadingDocuments(true);
    try {
      const requestPayload: Record<string, any> = {
        page: 1,
        pageSize: 1000,
        tailNumber,
        filters: {
          documentScope: "airframe",
          includeAttachedAssets: true,
        },
      };

      const response = await post<SearchDocumentsResponse>("api/documents/search", requestPayload, {
        showLoader: false,
        showErrorSnackbar: false,
        throwOnError: true,
      });

      const folders = response?.data?.data?.folders || [];
      const tree = buildFolderTree(folders);
      setFolderTree(tree);
      setExpandedFolderKeys(new Set(collectFolderKeys(tree)));
    } catch (error) {
      showError((error as Error).message || "Failed to load source documents.");
      setFolderTree([]);
    } finally {
      setLoadingDocuments(false);
    }
  }, [open, airframe, canLoadDocuments, post, tailNumber, showError]);

  useEffect(() => {
    void loadDocuments();
  }, [loadDocuments]);

  const allDocuments = useMemo(() => flattenTreeDocuments(folderTree), [folderTree]);
  const hasAnyDocuments = allDocuments.length > 0;

  const filteredTree = useMemo(() => {
    const term = pickerFilter.trim().toLowerCase();
    return filterFolderTree(folderTree, term);
  }, [folderTree, pickerFilter]);

  const canGenerate = templateAvailable && sections.some((section) => section.documents.length > 0);
  const activeSection = sections[activeSectionIndex];
  const documentsById = useMemo(() => {
    const map = new Map<string, DocumentOption>();
    allDocuments.forEach((doc) => {
      map.set(doc.documentId, doc);
    });
    return map;
  }, [allDocuments]);

  const selectedDocsForPanel = useMemo(() => {
    const fallbackById = new Map<string, MiniPackGenerationDocumentRequest>();
    if (activeSection) {
      activeSection.documents.forEach((doc) => {
        fallbackById.set(doc.documentId, doc);
      });
    }

    return Array.from(selectedDocumentIds)
      .map((id) => {
        const found = documentsById.get(id);
        if (found) {
          if (!isSupportedForMiniPack(found.fileName)) {
            return null;
          }

          return {
            documentId: found.documentId,
            fileName: found.fileName,
            displayName: found.displayName,
            folderPath: found.folderPath,
          };
        }

        const fallback = fallbackById.get(id);
        if (!fallback) {
          return null;
        }

        if (!isSupportedForMiniPack(fallback.fileName)) {
          return null;
        }

        return {
          documentId: fallback.documentId,
          fileName: fallback.fileName,
          displayName: fallback.displayName,
          folderPath: "",
        };
      })
      .filter((doc): doc is { documentId: string; fileName: string; displayName: string; folderPath: string } => Boolean(doc));
  }, [activeSection, selectedDocumentIds, documentsById]);

  const handleSectionChange = (index: number) => {
    if (!templateAvailable) {
      return;
    }

    const section = sections[index];
    setActiveSectionIndex(index);
    setPickerFilter("");
    setSelectedDocumentIds(
      new Set(
        section.documents
          .filter((doc) => isSupportedForMiniPack(doc.fileName))
          .map((doc) => doc.documentId)
      )
    );
  };

  const applySelectionToSection = async () => {
    if (!activeSection) {
      return;
    }

    const selected = selectedDocsForPanel.map((doc) => ({
      documentId: doc.documentId,
      fileName: doc.fileName,
      displayName: doc.displayName,
    }));

    const nextSections = [...sections];
    nextSections[activeSectionIndex] = {
      ...nextSections[activeSectionIndex],
      documents: selected,
    };

    setSections(nextSections);
    setSavingConfiguration(true);
    try {
      await persistConfiguration(nextSections);
      showSuccess(`${selected.length} document(s) attached to ${activeSection.sectionName}.`);
    } catch (error) {
      showError((error as Error).message || "Failed to save section attachments.");
    } finally {
      setSavingConfiguration(false);
    }
  };

  const handleDownloadLatestJob = async () => {
    if (!latestJob) return;

    try {
      const started = await downloadJob(latestJob);
      if (started) {
        showSuccess("Download started. Check your browser downloads.");
      } else {
        showError("Failed to start mini-pack download.");
      }
    } catch (error) {
      showError((error as Error).message || "Failed to download mini-pack.");
    }
  };

  const handleGenerate = async () => {
    if (!airframe || !canGenerate || !tailNumber) {
      if (!tailNumber) {
        showError("Tail number is required for airframe mini-pack generation.");
      }
      return;
    }

    const request: MiniPackGenerationRequest = {
      sourceScope: "airframe",
      tailNumber,
      sections: sections.map((section) => ({
        sectionKey: section.sectionKey,
        sectionName: section.sectionName,
        documents: section.documents,
      })),
    };

    setCreatingJob(true);
    try {
      await persistConfiguration(sections);
      await createJob(request);
      showSuccess("Mini-pack generation queued.");
      
      onOpenJobs(airframe);
      onClose();
    } catch (error) {
      showError((error as Error).message || "Failed to queue mini-pack generation.");
    } finally {
      setCreatingJob(false);
    }
  };

  const toggleFolderExpanded = (folderKey: string) => {
    setExpandedFolderKeys((previous) => {
      const next = new Set(previous);
      if (next.has(folderKey)) {
        next.delete(folderKey);
      } else {
        next.add(folderKey);
      }
      return next;
    });
  };

  const toggleDocumentSelection = (documentId: string, checked: boolean) => {
    setSelectedDocumentIds((previous) => {
      const next = new Set(previous);
      if (checked) {
        const doc = documentsById.get(documentId);
        if (!doc || !isSupportedForMiniPack(doc.fileName)) {
          return previous;
        }

        next.add(documentId);
      } else {
        next.delete(documentId);
      }
      return next;
    });
  };

  const toggleFolderSelection = (node: FolderTreeNode, checked: boolean) => {
    const documentIds = collectSelectableDocumentIds(node);
    setSelectedDocumentIds((previous) => {
      const next = new Set(previous);
      documentIds.forEach((id) => {
        if (checked) {
          next.add(id);
        } else {
          next.delete(id);
        }
      });
      return next;
    });
  };

  const renderTree = (nodes: FolderTreeNode[], depth = 0): React.ReactNode => {
    return nodes.map((node) => {
      const childDocumentIds = collectSelectableDocumentIds(node);
      const selectedCount = childDocumentIds.reduce(
        (count, id) => (selectedDocumentIds.has(id) ? count + 1 : count),
        0
      );
      const hasChildren = node.children.length > 0;
      const isExpanded = expandedFolderKeys.has(node.key);
      const hasDocumentsInNode = childDocumentIds.length > 0;

      return (
        <React.Fragment key={node.key}>
          <Box sx={documentTreeRowSx(depth)}>
            {hasChildren ? (
              <IconButton
                size="small"
                onClick={() => toggleFolderExpanded(node.key)}
                sx={{ color: COLORS.white }}
              >
                {isExpanded ? <ExpandMoreIcon fontSize="small" /> : <ChevronRightIcon fontSize="small" />}
              </IconButton>
            ) : (
              <Box sx={{ width: 32 }} />
            )}

            <Checkbox
              size="small"
              disabled={!hasDocumentsInNode}
              checked={hasDocumentsInNode && selectedCount === childDocumentIds.length}
              indeterminate={selectedCount > 0 && selectedCount < childDocumentIds.length}
              onChange={(event) => toggleFolderSelection(node, event.target.checked)}
              sx={{
                color: COLORS.mutedText,
                "&.Mui-checked, &.MuiCheckbox-indeterminate": {
                  color: COLORS.primary,
                },
              }}
            />

            <FolderIcon fontSize="small" sx={{ color: COLORS.primary }} />
            <Box sx={{ minWidth: 0 }}>
              <Typography sx={{ fontSize: 13, fontWeight: 700, color: COLORS.white }} noWrap>
                {node.name}
              </Typography>
              <Typography variant="caption" sx={{ color: COLORS.mutedText }} noWrap>
                {selectedCount}/{childDocumentIds.length} selected
              </Typography>
            </Box>
          </Box>

          {isExpanded &&
            node.documents.map((doc) => {
              const isSupported = isSupportedForMiniPack(doc.fileName);
              const checked = isSupported && selectedDocumentIds.has(doc.documentId);
              return (
                <Box
                  key={doc.documentId}
                  sx={{
                    ...documentTreeRowSx(depth + 1),
                    pl: 3 + (depth + 1) * 2,
                  }}
                >
                  <Box sx={{ width: 32 }} />
                  <Checkbox
                    size="small"
                    checked={checked}
                    disabled={!isSupported}
                    onChange={(event) => toggleDocumentSelection(doc.documentId, event.target.checked)}
                    sx={{
                      color: COLORS.mutedText,
                      "&.Mui-checked": {
                        color: COLORS.primary,
                      },
                    }}
                    title={!isSupported ? "Supported formats: PDF, PNG, JPEG, TXT" : undefined}
                  />
                  <DescriptionIcon fontSize="small" sx={{ color: COLORS.white }} />
                  <Box sx={{ minWidth: 0, opacity: isSupported ? 1 : 0.5 }}>
                    <Typography sx={{ fontSize: 13, color: COLORS.white }} noWrap title={isSupported ? undefined : "This file format is not supported for mini-pack PDF generation"}>
                      {doc.fileName}
                    </Typography>
                    <Typography variant="caption" sx={{ color: COLORS.mutedText }} noWrap>
                      {doc.folderPath}
                    </Typography>
                  </Box>
                </Box>
              );
            })}

          {isExpanded && hasChildren && renderTree(node.children, depth + 1)}
        </React.Fragment>
      );
    });
  };

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          width: { xs: "100%", md: 1160 },
          backgroundColor: "#111",
          color: "#fff",
          borderLeft: "1px solid #333",
        },
      }}
    >
      <Box sx={{ p: 2, height: "100%", display: "flex", flexDirection: "column", position: "relative" }}>
        {(savingConfiguration || creatingJob) && (
          <Box
            sx={{
              position: "fixed",
              inset: 0,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              backgroundColor: "rgba(17, 17, 17, 0.9)",
              zIndex: 1000,
              pointerEvents: "auto",
            }}
          >
            <CircularProgress size={50} sx={{ color: COLORS.primary }} />
            <Typography sx={{ color: "#fff", mt: 3, fontWeight: 600 }}>
              {savingConfiguration && !creatingJob ? "Saving..." : "Queuing PDF generation..."}
            </Typography>
          </Box>
        )}
        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
          <Box>
            <Typography variant="h6" sx={{ color: "hsl(19 101% 51% / 1)", fontWeight: 700 }}>
              Configure Mini-Pack
            </Typography>
            <Typography variant="body2" sx={{ color: "rgba(255,255,255,0.75)" }}>
              {scopeLabel || "Airframe"}
            </Typography>
          </Box>
          <IconButton onClick={onClose} sx={{ color: "#ccc" }}>
            <CloseIcon />
          </IconButton>
        </Stack>

        {hasPreviousMiniPack && latestJob && (
          <Alert
            severity="info"
            sx={{ mb: 1.5 }}
            action={
              <Button
                color="inherit"
                size="small"
                onClick={handleDownloadLatestJob}
                sx={{ textTransform: "none", fontWeight: 700 }}
              >
                Download Now
              </Button>
            }
          >
            A mini-pack has already been generated for this airframe.
          </Alert>
        )}

        {!isInitializing && !templateAvailable && (
          <Alert severity="warning" sx={{ mb: 1.5 }}>
            No Airframe mini-pack template is uploaded for this tenant. Upload the Airframe JSON template in Tenant Management before selecting documents.
          </Alert>
        )}

        {!isInitializing && templateAvailable && (
          <Alert severity="success" sx={{ mb: 1.5 }}>
            Airframe template loaded: {templateFileName || "Airframe.json"}
          </Alert>
        )}

        <Stack spacing={1} sx={{ flex: 1, minHeight: 0, overflow: "hidden", pr: 0.5 }}>
          {showWorkspace ? (
            <Stack
              direction={{ xs: "column", md: "row" }}
              spacing={1}
              sx={{
                flex: 1,
                minHeight: 0,
                overflow: "hidden",
              }}
            >
              <Paper
                sx={{
                  width: { xs: "100%", md: 230 },
                  backgroundColor: "#1a1a1a",
                  border: "1px solid #333",
                  p: 1,
                  overflowY: "auto",
                }}
              >
                <Typography sx={{ color: "#fff", fontWeight: 700, mb: 1 }}>Sections</Typography>
                {isInitializing ? (
                  <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
                    <CircularProgress size={24} sx={{ color: COLORS.primary }} />
                  </Box>
                ) : (
                  <Stack spacing={0.6}>
                    {sections.map((section, index) => {
                      const isActive = activeSectionIndex === index;
                      return (
                        <Button
                          key={section.templateSectionId || `${section.sectionKey}-${index}`}
                          fullWidth
                          onClick={() => handleSectionChange(index)}
                          sx={{
                            justifyContent: "space-between",
                            textTransform: "none",
                            fontWeight: isActive ? 700 : 500,
                            color: isActive ? "#111" : "#fff",
                            backgroundColor: isActive ? "hsl(19 101% 51% / 1)" : "transparent",
                            border: isActive ? "1px solid hsl(19 101% 51% / 1)" : "1px solid #444",
                            "&:hover": {
                              backgroundColor: isActive ? "hsl(19 101% 46% / 1)" : "#222",
                            },
                          }}
                        >
                          <Box sx={{ textAlign: "left", minWidth: 0 }}>
                            <Typography sx={{ fontSize: 13, fontWeight: "inherit" }} noWrap>
                              {section.sectionName}
                            </Typography>
                          </Box>
                          <Chip
                            label={section.documents.length}
                            size="small"
                            sx={{
                              height: 20,
                              color: isActive ? "#111" : "#fff",
                              backgroundColor: isActive ? "rgba(255,255,255,0.75)" : "#333",
                            }}
                          />
                        </Button>
                      );
                    })}
                  </Stack>
                )}
              </Paper>

              <Paper
                sx={{
                  flex: 1,
                  minWidth: 0,
                  backgroundColor: "#1a1a1a",
                  border: "1px solid #333",
                  p: 1,
                  display: "flex",
                  flexDirection: "column",
                  minHeight: 0,
                  position: "relative",
                  opacity: isInitializing ? 0.55 : 1,
                  pointerEvents: isInitializing ? "none" : "auto",
                }}
              >
                <Typography sx={{ color: "#fff", fontWeight: 700, mb: 1 }}>Document Browser</Typography>
                <TextField
                  fullWidth
                  placeholder="Search by file name, title, or folder path"
                  value={pickerFilter}
                  onChange={(event) => setPickerFilter(event.target.value)}
                  size="small"
                  sx={searchFieldSx}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon fontSize="small" sx={{ color: COLORS.white }} />
                      </InputAdornment>
                    ),
                  }}
                />

                {!hasAnyDocuments && (
                  <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.75)", display: "block", mb: 1 }}>
                    No documents found for this airframe yet. Upload documents first, then attach them under sections.
                  </Typography>
                )}

                <Box sx={{ ...documentTreeContainerSx, position: "relative" }}>
                  {loadingDocuments && (
                    <Box
                      sx={{
                        position: "absolute",
                        inset: 0,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        backgroundColor: "rgba(17, 17, 17, 0.72)",
                        zIndex: 1,
                      }}
                    >
                      <CircularProgress size={24} sx={{ color: COLORS.primary }} />
                    </Box>
                  )}
                  {!loadingDocuments && filteredTree.length === 0 ? (
                    <Typography sx={{ px: 2, py: 2, color: COLORS.mutedText }}>No documents found.</Typography>
                  ) : (
                    renderTree(filteredTree)
                  )}
                </Box>
              </Paper>

              <Paper
                sx={{
                  width: { xs: "100%", md: 290 },
                  backgroundColor: "#1a1a1a",
                  border: "1px solid #333",
                  p: 1,
                  display: "flex",
                  flexDirection: "column",
                  minHeight: 0,
                  opacity: isInitializing ? 0.55 : 1,
                  pointerEvents: isInitializing ? "none" : "auto",
                }}
              >
                <Typography sx={{ color: "#fff", fontWeight: 700, mb: 1 }}>
                  Selected Docs
                </Typography>
                <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.7)", mb: 1 }}>
                  {activeSection ? `For section: ${activeSection.sectionName}` : "Select a section"}
                </Typography>

                <Box sx={{ flex: 1, minHeight: 0, overflowY: "auto", border: "1px solid #333", borderRadius: 1, p: 0.75 }}>
                  {selectedDocsForPanel.length === 0 ? (
                    <Typography sx={{ fontSize: 13, color: "rgba(255,255,255,0.65)", px: 0.5, py: 0.75 }}>
                      No documents selected.
                    </Typography>
                  ) : (
                    <Stack spacing={0.6}>
                      {selectedDocsForPanel.map((doc) => (
                        <Stack
                          key={doc.documentId}
                          direction="row"
                          alignItems="center"
                          justifyContent="space-between"
                          sx={{
                            border: "1px solid #4a4a4a",
                            backgroundColor: "#242424",
                            borderRadius: 1,
                            px: 0.75,
                            py: 0.5,
                            gap: 1,
                          }}
                        >
                          <Box sx={{ minWidth: 0 }}>
                            <Typography
                              sx={{
                                fontSize: 12.5,
                                color: "#ffffff",
                                fontWeight: 600,
                                whiteSpace: "normal",
                                overflowWrap: "anywhere",
                                lineHeight: 1.35,
                              }}
                            >
                              {doc.fileName}
                            </Typography>
                          </Box>
                          <IconButton
                            size="small"
                            sx={{ color: "#d0d0d0" }}
                            onClick={() => toggleDocumentSelection(doc.documentId, false)}
                          >
                            <DeleteOutlineIcon fontSize="small" />
                          </IconButton>
                        </Stack>
                      ))}
                    </Stack>
                  )}
                </Box>

                <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
                  <Button
                    variant="outlined"
                    size="small"
                    fullWidth
                    onClick={() => setSelectedDocumentIds(new Set())}
                    sx={{
                      textTransform: "none",
                      color: "#ddd",
                      borderColor: "#555",
                    }}
                  >
                    Clear
                  </Button>
                  <Button
                    variant="contained"
                    size="small"
                    fullWidth
                    onClick={() => {
                      void applySelectionToSection();
                    }}
                    disabled={!activeSection || savingConfiguration}
                    startIcon={savingConfiguration ? <CircularProgress size={16} sx={{ color: "#fff" }} /> : undefined}
                    sx={{
                      textTransform: "none",
                      backgroundColor: "hsl(19 101% 51% / 1)",
                      "&:hover": { backgroundColor: "hsl(19 101% 41% / 1)" },
                    }}
                  >
                    {savingConfiguration ? "Attaching..." : "Attach"}
                  </Button>
                </Stack>
              </Paper>
            </Stack>
          ) : showPlaceholder ? (
            <Paper sx={{ backgroundColor: "#1a1a1a", border: "1px dashed #444", p: 2 }}>
              <Typography sx={{ color: "#fff", fontWeight: 600 }}>
                Upload an Airframe template to continue
              </Typography>
              <Typography variant="body2" sx={{ color: "rgba(255,255,255,0.7)", mt: 0.5 }}>
                Document selection is disabled until an Airframe JSON template is uploaded for this tenant.
              </Typography>
            </Paper>
          ) : null}
        </Stack>

        <Divider sx={{ my: 1.5, borderColor: "#333" }} />

        <Stack direction="row" spacing={1} justifyContent="space-between" alignItems="center">
          <Button
            variant="text"
            onClick={() => airframe && onOpenJobs(airframe)}
            disabled={!airframe}
            sx={{ textTransform: "none", color: "#bbb" }}
          >
            View Jobs
          </Button>

          <Button
            variant="contained"
            startIcon={creatingJob ? <CircularProgress size={16} sx={{ color: "#fff" }} /> : <SaveIcon />}
            disabled={creatingJob || savingConfiguration || isInitializing || !canGenerate || !tailNumber}
            onClick={handleGenerate}
            sx={{
              textTransform: "none",
              backgroundColor: "hsl(19 101% 51% / 1)",
              "&:hover": { backgroundColor: "hsl(19 101% 41% / 1)" },
            }}
          >
            {creatingJob ? "Queuing..." : "Save & Generate PDF"}
          </Button>
        </Stack>
      </Box>
    </Drawer>
  );
};
